import {
  formatClientInvoiceAmount,
  formatClientInvoiceDate,
  normalizeClientInvoiceRow,
} from "../../../../services/clientInvoiceContract";

const numberValue = (value, fallback = 0) => {
  if (value && typeof value === "object" && value.amount_minor !== undefined) {
    const minor = Number(value.amount_minor);
    return Number.isFinite(minor) ? minor / 100 : fallback;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};
const firstText = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

/** The free-text NOTE the salon typed on the sale, when one was saved. */
export const clientInvoiceNote = (row) => String(firstText(row?.description, row?.note) || "").trim();

const fullName = (person) => person && [person.first_name, person.last_name].filter(Boolean).join(" ");

const lineName = (line) => firstText(
  line.name,
  line.service?.name,
  line.services?.name,
  line.product?.name,
  line.products?.name,
  line.membership?.name,
  line.subscription?.name,
  line.voucherto?.name,
  line.type,
  "Item",
);

const appointmentDate = (appointment) => formatClientInvoiceDate(
  appointment?.dateof || appointment?.start_datetime || appointment?.created_at,
);

const appointmentTime = (appointment) => {
  const start = firstText(appointment?.start_time, appointment?.start_datetime?.slice?.(11, 19));
  const end = firstText(appointment?.end_time, appointment?.end_datetime?.slice?.(11, 19));
  return [start, end].filter(Boolean).join(" - ");
};

const toDisplayLine = (line, appointment) => {
  const quantity = numberValue(line?.quantity ?? line?.qty, 1) || 1;
  const total = numberValue(line?.line_total ?? line?.cost ?? line?.total, 0);
  const unit = numberValue(line?.unit_amount ?? line?.cost ?? line?.total, quantity ? total / quantity : total);
  const staffName = fullName(line?.staff) || fullName(appointment?.staff);
  return {
    id: line?.id ?? appointment?.id,
    name: lineName(line || appointment || {}),
    type: firstText(line?.type, line?.item?.type, "Service"),
    quantity,
    unit,
    total,
    staffName,
    appointmentDate: appointmentDate(appointment || line),
    appointmentTime: appointmentTime(appointment || line),
  };
};

const depositRefundStatus = (invoice) => {
  const evidence = invoice.deposit_refund;
  const fallback = firstText(invoice.payment_state, invoice.status, "unknown");
  const currency = String(invoice.currency || invoice.total?.currency || "").toUpperCase();
  if (!evidence || !currency || evidence.currency !== currency
    || [evidence.paid_amount_minor, evidence.refunded_amount_minor, evidence.sale_paid_amount_minor].some(value => value === null || value === undefined || !Number.isFinite(Number(value)))
    || Number(evidence.paid_amount_minor) <= 0 || Number(evidence.refunded_amount_minor) <= 0
    || Number(evidence.sale_paid_amount_minor) > 0
    || ["paid", "completed"].includes(String(invoice.status).toLowerCase())) return fallback;
  return Number(evidence.refunded_amount_minor) >= Number(evidence.paid_amount_minor) ? "Refunded" : "Partial";
};

export const buildClientInvoiceDisplay = (detail, options = {}) => {
  const normalized = normalizeClientInvoiceRow(detail || {}, { client_id: detail?.client_id });
  const total = numberValue(normalized.totalprice ?? normalized.total, 0);
  const paid = numberValue(normalized.total_pay ?? normalized.paid, 0);
  const explicitBalance = normalized.balance ?? normalized.amount_due;
  const balance = Math.max(0, numberValue(explicitBalance, total - paid));
  const appointments = Array.isArray(normalized.appointments) ? normalized.appointments : [];
  const cart = Array.isArray(normalized.cart) ? normalized.cart : [];
  const lines = cart.length
    ? cart.map((line, index) => toDisplayLine(line, appointments[index] || appointments.find((item) => item.service_id === line.item?.id)))
    : appointments.map((appointment) => toDisplayLine(appointment, appointment));
  const taxPercentage = numberValue(options.taxPercentage, 0);
  const storedTax = normalized.tax !== undefined && normalized.tax !== null && normalized.tax !== ""
    ? numberValue(normalized.tax, NaN) : NaN;
  // Canonical tax is authoritative. Legacy payloads without it use the saved
  // inclusive divisor (11 means gross / 11, not an eleven-percent tax rate).
  const gst = total === 0 ? 0 : Number.isFinite(storedTax)
    ? storedTax : taxPercentage > 0 ? total / taxPercentage : 0;
  const refundFlag = normalized.is_refundable ?? normalized.refundable;
  const refunded = (Array.isArray(normalized.refunds) ? normalized.refunds : []).reduce(
    (sum, refund) => sum + (refund.total_refunded_minor !== undefined
      ? numberValue(refund.total_refunded_minor) / 100
      : numberValue(refund.total_refunded_amount)), 0,
  );
  const hasPositiveRefundableBalance = Array.isArray(normalized.refund_sources)
    ? normalized.refund_sources.some(source => Number(source.refundable_amount_minor) > 0)
    : paid > refunded;
  const canRefund = (refundFlag === true || refundFlag === 1 || String(refundFlag).toLowerCase() === "true" || (refundFlag === undefined && hasPositiveRefundableBalance)) && hasPositiveRefundableBalance && normalized.status !== "Schedule";

  return {
    invoiceNumber: firstText(normalized.invoice_number, normalized.number, normalized.id !== undefined ? `#${normalized.id}` : "—"),
    invoiceDate: formatClientInvoiceDate(normalized.invoicedate || normalized.invoice_date || normalized.created_at),
    clientId: normalized.client_id ?? normalized.client?.id,
    clientName: [normalized.client?.first_name, normalized.client?.last_name].filter(Boolean).join(" "),
    status: depositRefundStatus(normalized),
    lines,
    subtotal: numberValue(normalized.cart_sum_cost, total),
    gst,
    total,
    paid,
    balance,
    paymentStatus: depositRefundStatus(normalized),
    canRefund,
    totalLabel: formatClientInvoiceAmount(total),
    note: clientInvoiceNote(normalized),
  };
};
